import { Box, Button, CircularProgress, Typography } from "@mui/material";
import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import ReplayIcon from "@mui/icons-material/Replay";

interface IStreamProps {
	cameraId: number;
}

// const WS_BASE_URL = `ws://100.64.1.57/dws/video`;
const WS_BASE_URL = `ws${window.location.protocol.replace("http", "")}//${window.location.hostname}/dws/video`;

const Stream: FC<IStreamProps> = (props) => {
	const { cameraId } = props;

	const webSocketRef = useRef<WebSocket | null>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const [imageData, setImageData] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState<string>("");

	function requestFrame() {
		if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
			webSocketRef.current.send(JSON.stringify({ event_type: "start_stream" }));
		}
	}

	const connectWebSocket = useCallback(() => {
		setLoading(true);

		webSocketRef.current = new WebSocket(`${WS_BASE_URL}/${cameraId}/`);

		webSocketRef.current.onopen = () => {
			console.log("Connected to WS server");
			requestFrame();
		};

		webSocketRef.current.onmessage = (event) => {
			if (event.data instanceof Blob) {
				setImageData(URL.createObjectURL(event.data));
				setLoading(false);
				setErrorMessage("");
			} else {
				const data = JSON.parse(event.data);

				if ("type" in data && (data.type === "send_error_message" || data.type === "send_stream_end_message")) {
					setErrorMessage(
						data.type === "send_stream_end_message" ? "Stream ended" : data?.data?.info || "Something went wrong"
					);
					setLoading(false);
				} else if ("frame_data" in data) {
					setImageData(data.frame_data);
					setLoading(false);
					setErrorMessage("");
				} else if ("info" in data) {
					setLoading(false);
					setErrorMessage("");
				}
			}
		};

		webSocketRef.current.onclose = () => {
			console.log("WebSocket closed");

			timeoutRef.current = setTimeout(() => {
				connectWebSocket();
			}, 1000);
		};

		webSocketRef.current.onerror = (error) => {
			console.error("WebSocket error:", error);
			if (webSocketRef.current) webSocketRef.current.close();
		};
	}, [cameraId]);

	function disconnectWebSocket() {
		if (webSocketRef.current) {
			webSocketRef.current.onclose = () => {};
			webSocketRef.current.close();
		}

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}

	function handleRetryButtonClick() {
		disconnectWebSocket();
		connectWebSocket();
	}

	useEffect(() => {
		return () => {
			disconnectWebSocket();
		};
	}, []);

	useEffect(() => {
		if (!cameraId) return;

		disconnectWebSocket();
		connectWebSocket();
	}, [cameraId, connectWebSocket]);

	if (loading) {
		return (
			<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
				<CircularProgress />
			</Box>
		);
	}

	if (errorMessage) {
		return (
			<Box
				sx={{
					width: "100%",
					height: "100%",
					display: "flex",
					flexDirection: "column",
					alignItems: "center",
					justifyContent: "center",
					gap: "1rem"
				}}
			>
				<Typography>{errorMessage}</Typography>

				<Button
					disableElevation
					variant="contained"
					color="primary"
					startIcon={<ReplayIcon />}
					onClick={handleRetryButtonClick}
				>
					Retry
				</Button>
			</Box>
		);
	}

	if (!imageData) {
		return (
			<Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
				<Typography>Stream not found</Typography>
			</Box>
		);
	}

	return (
		<img
			// src={`data:image/jpeg;base64,${imageData}`}
			src={imageData.startsWith("blob:") ? imageData : `data:image/jpeg;base64,${imageData}`}
			alt="Stream"
			style={{ objectFit: "contain", width: "100%", height: "100%" }}
		/>
	);
};

export default Stream;
